import React, { useEffect, useState } from 'react'

import { AppSetting } from '../../types/appSetting';

import { Icons } from '../icons';
import { UtilityService } from '../../services/utilityService';
import { Button, Checkbox, FormControlLabel, MenuItem, Select, styled, TextField, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { AppSettingService } from '../../services/appSettingService';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export interface IAppSettingTableProps {
	data?: AppSetting[];
}

const AppSettingTable: React.FunctionComponent<IAppSettingTableProps> = ({ data }) => {
	const [dataView, setDataView] = useState<AppSetting[]>([]);
	const [firstVisibleRow, setFirstVisibleRow] = useState(0);
	const [lastVisibleRow, setLastVisibleRow] = useState(9);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState("name");
	const [sortAscending, setSortAscending] = useState(true);
	const [isEditing, setIsEditing] = useState(false);
	const [editingItem, setEditingItem] = useState<AppSetting>(null);
	const [tempEditValue, setTempEditValue] = useState<any>("");
	const [tempEditType, setTempEditType] = useState("");
	const [tempEditMask, setTempEditMask] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [validationError, setValidationError] = useState<string | undefined>();

	const supportedDataTypes = ["text", "number", "date", "switch"];
	const copyableDataTypes = ["text", "number", "date"];
	const maskableDataTypes = ["text", "number", "date"];

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.arrow}`]: {
			color: theme.palette.common.black,

  		},
		[`& .${tooltipClasses.tooltip}`]: {
			maxWidth: 220,
			fontSize: theme.typography.pxToRem(12),
			backgroundColor: theme.palette.common.black,
			color: theme.palette.common.white,
		},
	}));

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	useEffect(() => {
		if (data) {
			let newData = data.slice();

			if (sortField) {
				switch (sortField) {
					case "name": {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
					case "value": {
						newData.sort((a, b) => UtilityService.compareStrings(a.mask ? null : a.value, b.mask ? null : b.value));
						break;
					}
					case "type": {
						newData.sort((a, b) => UtilityService.compareStrings(a.type, b.type));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
				}

				if (!sortAscending) newData.reverse();

				setDataView(newData);
				setFirstVisibleRow(0);
				setLastVisibleRow(pageSize - 1);
			}
		}
	}, [data, sortField, sortAscending])

	useEffect(() => {
		if (isEditing && editingItem && editingItem.type != tempEditType) {
			// If we change the type away from a boolean, reset the value so the user doesn't see true/false
			if (editingItem.type == "switch") setTempEditValue('');
		}
	}, [tempEditType])

	const handlePageBack = () => {
		setFirstVisibleRow(firstVisibleRow - pageSize);
		setLastVisibleRow(lastVisibleRow - pageSize);
	}

	const handlePageForward = () => {
		setFirstVisibleRow(firstVisibleRow + pageSize);
		setLastVisibleRow(lastVisibleRow + pageSize);
	}

	const handleEdit = (item: AppSetting, target: EventTarget) => {
		setEditingItem(item);
		setTempEditType(supportedDataTypes.find(type => type === item.type.toLowerCase()) || "text");
		setTempEditValue(item.mask ? '' : item.value);
		setTempEditMask(item.mask || false);
		setIsEditing(true);
	}

	const handleCancel = () => {
		setIsEditing(false);
		setEditingItem(null);
		setValidationError(undefined);
	}

	const handleSave = () => {
		setValidationError(undefined);
		if (tempEditType == "date" && !dayjs(tempEditValue).isValid()) {
			setValidationError("Invalid date");
			return;
		}
		else if (tempEditValue.toString().trim() == "") {
			setValidationError("Value cannot be empty");
			return;
		}

		setIsSaving(true);

		let updatedItem = Object.assign({}, editingItem);
		updatedItem.value = tempEditValue;
		updatedItem.type = tempEditType;
		updatedItem.mask = maskableDataTypes.includes(tempEditType) ? tempEditMask : false;

		AppSettingService.setAppSetting(updatedItem)
			.then((response) => {
				console.log('AppSettingService.setAppSetting: SUCCESS', response);
				editingItem.value = tempEditValue;
				editingItem.type = tempEditType;
				editingItem.mask = tempEditMask;
				setIsEditing(false);
				setEditingItem(null);	
			})
			.catch((error) => {
				console.log('AppSettingService.setAppSetting: ERROR', error);
				alert("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);			
			});
	}

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSave();
		}
		else if (event.key === "Escape") {
			handleCancel();
		}
	}

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(function() {
			console.log('Copying to clipboard was successful!');
		}, function(err) {
			console.error('Could not copy text: ', err);
		});
	}

	const valueFilter = (appSetting: AppSetting): string => {
		let transformedValue: string = appSetting.value;
		switch (appSetting.type.toLowerCase()) {
			case "switch": {
				transformedValue = appSetting.value === "true" ? "ON" : "OFF";
			}
		}

		return appSetting.mask ? "••••••••••••••••" : transformedValue;
	}

	const canCopy = (appSetting: AppSetting): boolean => {
		if (appSetting.mask ?? false) return false;
		return copyableDataTypes.includes(appSetting.type.toLowerCase());
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="name-col">
					<p onClick={() => handleHeaderClick("name")}>
						Name
						{" "}
						{(sortField == "name" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "name" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="value-col">
					<p onClick={() => handleHeaderClick("value")}>
						Value
						{" "}
						{(sortField == "value" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "value" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="type-col">
					<p onClick={() => handleHeaderClick("type")}>
						Type
						{" "}
						{(sortField == "type" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "type" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>	
				<div className="controls-col">{""}</div>
			</div>
			<div className="data">
				{dataView?.length > 0 ?
					(
						<>
						{dataView?.map((item, index) => (
							<div className={index >= firstVisibleRow && index <= lastVisibleRow ? "row" : "row row-hidden"} key={item.id}>
								<div className="name-col"><p>{item.name}</p></div>
								{!(isEditing && editingItem?.id == item.id) &&
									<div className="value-col">
										<p className={item.mask ? 'masked' : ''}>{valueFilter(item)}</p>
										{item.mask &&
											<div className='mask-caption'>Masked</div>
										}
										{canCopy(item) && 
											<Button variant="outlined" color="info" size='small' className="copy-button" onClick={() => copyToClipboard(item.value)}>Copy</Button>
										}
									</div>
								}
								{(isEditing && editingItem?.id == item.id) &&
									<>
									{tempEditType == "text" &&
										<Tooltip
											title={validationError} 
											placement='left' 
											arrow={true}
											disableHoverListener={true}
											disableFocusListener={true}
											open={validationError != undefined}>
												<div className="value-edit-col">
													<p className='textbox-width'>
														<TextField 
															autoFocus={true}
															name="value" 
															placeholder={"Text for " + item.name} 
															required 
															value={tempEditValue} 
															size="small" 
															disabled={isSaving} 
															onFocus={(event => event.target.select())}
															onChange={(event) => { setTempEditValue(event.target.value.substring(0, 200)); setValidationError(undefined) }}
															onKeyDown={(event) => handleKeyDown(event)}>
														</TextField>
													</p>
													<FormControlLabel control={<Checkbox checked={tempEditMask} onChange={(event) => setTempEditMask(event.target.checked)}></Checkbox>} label="Mask" />
												</div>
										</Tooltip>
									}
									{tempEditType == "number" &&
										<Tooltip
											title={validationError} 
											placement='top' 
											arrow={true}
											disableHoverListener={true}
											open={validationError != undefined}>
											<div className="value-edit-col">
												<p className='textbox-width'>
													<TextField 
														autoFocus={true}
														name="value" 
														type="number"
														placeholder={"Number for " + item.name} 
														required 
														value={tempEditValue} 
														size="small" 
														disabled={isSaving} 
														onFocus={(event => event.target.select())}
														onChange={(event) => { setTempEditValue(event.target.value.substring(0, 200)); setValidationError(undefined) }}
														onKeyDown={(event) => handleKeyDown(event)}>
													</TextField>
												</p>
												<FormControlLabel control={<Checkbox checked={tempEditMask} onChange={(event) => setTempEditMask(event.target.checked)}></Checkbox>} label="Mask" />
											</div>
										</Tooltip>
									}
									{tempEditType == "date" &&
										<Tooltip
											title={validationError} 
											placement='top' 
											arrow={true}
											disableHoverListener={true}
											open={validationError != undefined}>
											<div className="value-edit-col">
												<p className='date-width'>
													<LocalizationProvider dateAdapter={AdapterDayjs}>
														<DatePicker
															className="end-date-field"
															value={dayjs(tempEditValue)}
															onChange={(value) => { setTempEditValue(value.format("MMMM DD, YYYY")) }}
															onClose={() => { setValidationError(undefined) }}
															slotProps={{
																textField: {
																	required: true,
																},
															}}
														/>
													</LocalizationProvider>	
												</p>
												<FormControlLabel control={<Checkbox checked={tempEditMask} onChange={(event) => setTempEditMask(event.target.checked)}></Checkbox>} label="Mask" />
											</div>
										</Tooltip>
									}
									{tempEditType == "switch" &&
										<Tooltip
											title={validationError} 
											placement='top' 
											arrow={true}
											disableHoverListener={true}
											open={validationError != undefined}>
											<div className="value-edit-col">
												<p>
													<FormControlLabel control={<Checkbox checked={tempEditValue === "true"} onChange={(event) => setTempEditValue(event.target.checked ? "true" : "false")}></Checkbox>} label={tempEditValue === "true" ? "ON" : "OFF"} />
												</p>
											</div>
										</Tooltip>
									}
									</>
								}
								{!(isEditing && editingItem?.id == item.id) &&
									<div className={"type-col" + (supportedDataTypes.find(st => st === item.type.toLowerCase()) == undefined ? " error" : "")}>
										<p>
											<HtmlTooltip 
												title={
													<React.Fragment>
														<center>
															<div>
																<Typography fontSize={".875rem"}>This type is not supported.</Typography>
																It will default to &quot;text&quot; if modified.
															</div>
														</center>
													</React.Fragment>
												} 
												role='tooltip-appsetting-type'
												placement='right' 
												arrow={true}
												disableHoverListener={supportedDataTypes.find(st => st === item.type.toLowerCase()) != undefined}>
												<span>{item.type}</span>
											</HtmlTooltip>
										</p>
									</div>
								}
								{(isEditing && editingItem?.id == item.id) &&
									<div className="type-edit-col">
										<p>
											<Select
												size="small"
												value={tempEditType}
												required
												onChange={(event) => { setTempEditType(event.target.value) }}
											>
												{supportedDataTypes.map((type, index) => (
													<MenuItem value={type} key={index}>{type}</MenuItem>
												))}
											</Select>
										</p>
									</div>
								}
								<div className="controls-col">
									<p>
										{!(isEditing && editingItem?.id == item.id) &&
											<Button 
												type="button" 
												variant="text" 
												className="edit-button" 
												onClick={() => handleEdit(item, event.target)} 
												disabled={isEditing && editingItem?.id != item.id}>
												{(<><Icons.EditItem fill="#272f50" height="16px"></Icons.EditItem><span>&nbsp;Edit</span></>)}
											</Button>
										}
										{isEditing && editingItem?.id == item.id &&
											<>
												{!isSaving &&
													<Button 
														type="button" 
														variant="text" 
														className="cancel-button" 
														onClick={() => handleCancel()} 
														disabled={isSaving}>
														{(<><Icons.RemoveItem fill="#272f50" height="16px"></Icons.RemoveItem><span>&nbsp;Cancel</span></>)}
													</Button>
												}
												<Button 
													type="button" 
													variant="text" 
													className="save-button" 
													onClick={() => handleSave()} 
													disabled={isSaving}>
													{isSaving ? "Saving..." : (<><Icons.AddItem fill="#272f50" height="16px"></Icons.AddItem><span>&nbsp;Save</span></>)}
												</Button>
											</>
										}
									</p>
								</div>
							</div>
							
						))}
						</>
					) : (<div className="no-results">0 results</div>)}
			</div>
			<div className="pager">
				<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={firstVisibleRow == 0 ? true : false} onClick={handlePageBack}>Previous</Button></div>
				<div className="pager-text">{`${firstVisibleRow + 1} to ${(lastVisibleRow + 1) <= dataView.length ? lastVisibleRow + 1 : dataView.length} of ${dataView.length}`}</div>
				<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={lastVisibleRow >= dataView.length - 1 ? true : false} onClick={handlePageForward}>Next</Button></div>
			</div>
		</div>
	)
}

export default AppSettingTable;